import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  styled,
  SxProps,
  Theme,
} from '@mui/material';
import { ListItemProps } from '@mui/material/ListItem';
import { Timestamp } from 'firebase/firestore';
import React from 'react';
import { MultilineText, UnreadBadge } from '@xolv-mybrightlink/common';
import { LastMessageTime } from './LastMessageTime';
import { is240DaysOld } from '../hooks/firebase/common';

type ChannelCardProps = {
  avatarName?: string;
  avatar?: React.ReactNode;
  name: string;
  lastMessage?: string;
  lastMessageDate?: Timestamp;
  unreadCount?: number;
  selected?: boolean;
  onSelect: () => void;
};

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active',
})<ListItemProps & { active?: boolean }>((props) => {
  if (!props.active) {
    return {
      '&:hover': {
        backgroundColor: props.theme.palette.action.hover,
      },
    };
  }
});

export const ChannelCard = (props: ChannelCardProps) => {
  const cardStyle: SxProps<Theme> = {
    alignItems: 'flex-start',
    cursor: 'pointer',
    px: 5,
    py: 2.5,
    ...(props.selected && { bgcolor: '#307fe2', color: 'white' }),
  };
  const timeStyle: SxProps<Theme> = {
    color: '#898f9b',
    width: 75,
    alignSelf: 'center',
    textAlign: 'end',
    fontSize: 14,
    ...(props.selected && { color: 'white' }),
  };
  const lastMessageStyle: SxProps<Theme> = {
    fontSize: 12,
    margin: 0,
    color: '#626a7a',
    wordBreak: 'break-word',
    ...(props.selected && { color: 'white' }),
  };

  const { lastMessageDate } = props;

  return (
    <StyledListItem
      sx={cardStyle}
      onClick={props.onSelect}
      active={props.selected}
    >
      <ListItemAvatar sx={{ minWidth: 42 }}>
        <Avatar
          sx={{
            bgcolor: '#626a7a',
            width: 32,
            height: 32,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {props.avatar ?? props.avatarName ?? ''}
          </Box>
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{ m: 0 }}
        primary={
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ flex: 1 }}>
              <MultilineText
                maxLines={1}
                sx={{ fontSize: 14, fontWeight: 600 }}
              >
                {props.name}
              </MultilineText>
            </Box>
            <Box sx={timeStyle}>
              <LastMessageTime timestamp={lastMessageDate?.toDate()} />
            </Box>
          </Box>
        }
        secondary={
          lastMessageDate &&
          !is240DaysOld(lastMessageDate.toDate()) && (
            <Box display='flex'>
              <Box flex='1'>
                <MultilineText maxLines={2} sx={lastMessageStyle}>
                  {props.lastMessage}
                </MultilineText>
              </Box>
              <Box
                sx={{
                  width: 75,
                  alignSelf: 'center',
                  textAlign: 'end',
                  fontSize: 14,
                  color: '#898f9b',
                  pr: 2.5,
                }}
              >
                <UnreadBadge count={props.unreadCount} />
              </Box>
            </Box>
          )
        }
        primaryTypographyProps={{ component: 'div', sx: { color: 'inherit' } }}
        secondaryTypographyProps={{
          component: 'div',
          sx: { color: 'inherit', mt: 1 },
        }}
      />
    </StyledListItem>
  );
};
